import { ChangeEvent, ChangeEventHandler, useState } from "react";
import produce from "immer";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Box, TextField, Typography } from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import AutocompleteSearchInput from "components/AutocompleteSearchInput";
import useNotificationVariables from "pages/Rules/hooks/useNotificationVariables";
import { ErrorMessage } from "../../Actions/Styled";

import { useRuleContext } from "../../../context/ruleContext";
import { Field, FormFields } from "../../../../common/FormFields";

import { SenderNotification } from "@dashboard-v3/api";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";
import { getApprovalSteps } from "pages/Rules/Form/context/ruleSelectors";

export default function SenderNotificationSettings() {
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { senderNotification } = getApprovalSteps(state.rule);
  const [isEnabled, setIsEnabled] = useState(Boolean(senderNotification));
  const { variables, replaceIdsWithLabels, replaceLabelsWithIds } =
    useNotificationVariables({
      ruleType: "SEND_DELAY",
      notificationType: "SENDER",
    });
  const titleError = hasError(
    "SENDER_TITLE_REQUIRED",
    "SENDER_TITLE_MAX_LENGTH"
  );
  const bodyError = hasError(
    "SENDER_BODYTEXT_REQUIRED",
    "SENDER_BODYTEXT_MAX_LENGTH"
  );
  const accessLinkError = hasError("SENDER_ACCESSLINK_FIELD_ERROR");
  const cancelLinkError = hasError("SENDER_CANCELLINK_FIELD_ERROR");

  const updateSettings = (update: Partial<SenderNotification>) => {
    dispatch({
      type: "updateSendDelay",
      payload: { senderNotification: update },
    });
  };

  function handleToggleChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, checked } = event.target;

    const update = produce(senderNotification, draft => {
      draft[name] = checked;
    });

    updateSettings(update);
  }

  function handleTextChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    const update = produce(senderNotification, draft => {
      draft[name] = value;
    });

    updateSettings(update);
  }

  function handleNotificationBody(value: string) {
    const update = produce(senderNotification, draft => {
      draft.bodyText = replaceLabelsWithIds(value);
    });

    updateSettings(update);
  }

  const onEnableNotification: ChangeEventHandler<HTMLInputElement> = e => {
    const { checked } = e.target;
    setIsEnabled(checked);

    if (!checked) return updateSettings(null);

    updateSettings({
      title: t("sendDelay.senderNotification.titleDefault"),
      bodyText: t("sendDelay.senderNotification.bodyDefault"),
      accessLink: true,
      accessLinkLabel: t("sendDelay.senderNotification.accessLinkDefault"),
      cancelLink: true,
      cancelLinkLabel: t("sendDelay.senderNotification.cancelLinkDefault"),
    });
  };

  function hasError(...errorCodes: string[]) {
    const errors = (state.steps.sendDelay?.error || []) as string[];

    return errors.some(error => errorCodes.includes(error));
  }

  return (
    <Box marginBottom="15px" marginTop="5px">
      <SwitchField
        label={t("sendDelay.senderNotification.enableLabel")}
        value={isEnabled}
        onChange={onEnableNotification}
        style={{ marginBottom: isEnabled ? 10 : 0 }}
        description={!isEnabled && t("sendDelay.senderNotification.enableDesc")}
      />
      {isEnabled && (
        <div style={{ padding: "8px 16px 16px" }}>
          <FormFields>
            <Field>
              <TextField
                name="title"
                variant="outlined"
                label={t("sendDelay.senderNotification.titleLabel")}
                value={senderNotification.title}
                onChange={handleTextChange}
                error={titleError}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.TITLE }}
              />
              <ErrorMessage
                isError={titleError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>
            <Field>
              <Typography
                variant="caption"
                color={bodyError ? "error" : "textSecondary"}
              >
                {t("sendDelay.senderNotification.bodyLabel")}
              </Typography>
              <AutocompleteSearchInput
                name="bodyText"
                multiple
                rows={5}
                fullWidth
                variables={variables}
                error={bodyError}
                maxLength={NOTIFICATION_LIMITS.BODY_TEXT}
                value={replaceIdsWithLabels(senderNotification.bodyText)}
                onChange={handleNotificationBody}
              />
              <ErrorMessage
                isError={bodyError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>
            <SwitchField
              name="accessLink"
              label={t("sendDelay.senderNotification.accessLink.label")}
              description={t("sendDelay.senderNotification.accessLink.desc")}
              value={senderNotification.accessLink}
              onChange={handleToggleChange}
            />

            <Field>
              <TextField
                name="accessLinkLabel"
                variant="outlined"
                label={t("sendDelay.senderNotification.accessLinkLabel")}
                value={senderNotification.accessLinkLabel}
                onChange={handleTextChange}
                disabled={!senderNotification.accessLink}
                error={accessLinkError}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.CTA_LABEL }}
              />
              <ErrorMessage
                isError={accessLinkError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>

            <SwitchField
              name="cancelLink"
              label={t("sendDelay.senderNotification.cancelLink.label")}
              description={t("sendDelay.senderNotification.cancelLink.desc")}
              value={senderNotification.cancelLink}
              onChange={handleToggleChange}
            />
            <Field>
              <TextField
                name="cancelLinkLabel"
                variant="outlined"
                label={t("sendDelay.senderNotification.cancelLinkLabel")}
                value={senderNotification.cancelLinkLabel}
                onChange={handleTextChange}
                disabled={!senderNotification.cancelLink}
                error={cancelLinkError}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.CTA_LABEL }}
              />
              <ErrorMessage
                isError={cancelLinkError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>
          </FormFields>
        </div>
      )}
    </Box>
  );
}

export const SettingFieldsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  > .MuiFormControl-root.MuiTextField-root {
    max-width: 90%;
  }
`;

export const SettingsSwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  .MuiFormControl-root.MuiTextField-root {
    margin-left: 3%;
    max-width: 87%;
  }
`;
